"use client";
import UKToast from "@/components/UKToast";
import IntlDialog from "@/components/intlMui/dialog";
import { useTranslation } from "@/i18n/client";
import { Radio, RadioGroup, Typography, FormControl, FormControlLabel } from "@mui/material";
import { IntlPopover } from "@/components/intlMui";
import { callAfterLogin } from "@/lib/jdiClientUtils";
import { useState, Fragment, useEffect, forwardRef, useImperativeHandle, useRef } from "react";
import { getReportReason, reportComment, handleDeleteComment, setAnonymousFlag } from "@/apis/service";
import { sendClickTrack, EXPOSURE_RECORD } from "@/tracks/25217";
import { dataExpTagConfig } from "@/components/product/const";
import { EXPOSURE_RECORD as REVIEW_LIST_EXPOSURE_RECORD, sendClickTrack as reviewListSendClickTrack } from "@/tracks/25338";
import { sendClickTrack as reviewDetailSendClickTrack, EXPOSURE_RECORD as REVIEW_DETAIL_EXPOSURE_RECORD } from "@/tracks/25347";
import { t } from "i18next";

const FormContent = forwardRef<HTMLDivElement>((props, ref) => {
  // 举报弹窗表单数据
  const [formFieldValue, setFormFieldValue] = useState({
    reportReason: { value: "1" },
    reportContent: { value: "" },
  });
  // 举报原因列表
  const [reasonList, setReasonList] = useState<any[]>([]);
  const { t } = useTranslation();
  // 举报内容输入框
  const onTextareaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value.length >= 200) return;
    setFormFieldValue({ ...formFieldValue, reportContent: { value: (event.target as HTMLInputElement).value } });
  };
  // 举报原因选择
  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormFieldValue({ ...formFieldValue, reportReason: { value: (event.target as HTMLInputElement).value } });
  };

  // 获取举报原因列表
  const getReportReasonList = async () => {
    const { reasonList: resReasonList = [] } = (await getReportReason()) || {};
    setReasonList(resReasonList);
  };
  // 获取表单数据
  useImperativeHandle(
    ref,
    () => {
      return {
        getFormFieldValue: () => {
          return formFieldValue;
        },
      } as any;
    },
    [formFieldValue],
  );
  // 获取举报原因列表
  useEffect(() => {
    getReportReasonList();
  }, []);

  // 渲染举报弹窗
  return (
    <div className="report-form-wrapper">
      <FormControl>
        <label id="demo-radio-buttons-group-label" className="reason-label">
          {t("dp.reporting.reportingReason")}
        </label>
        <RadioGroup
          aria-labelledby="demo-radio-buttons-group-label"
          value={formFieldValue["reportReason"].value}
          name="reportReason"
          onChange={handleRadioChange}
          sx={{
            "& .MuiRadio-root": {
              color: "rgba(224, 225, 229, 1)",
            },
          }}
        >
          {reasonList?.map((reason, index) => {
            return <FormControlLabel key={index} value={reason.id} control={<Radio />} label={reason.title} />;
          })}
        </RadioGroup>
      </FormControl>
      <div className="comment-textarea">
        <p className="title">{t("dp.reporting.detailDesc")}</p>
        <textarea value={formFieldValue["reportContent"].value} onChange={onTextareaChange} placeholder={t("dp.reporting.detailDescPlaceholder")}></textarea>
        <span className="num">{formFieldValue.reportContent.value.length + 1}/200</span>
      </div>
    </div>
  );
});
FormContent.displayName = "FormContent";
export default function MenuReporting({ reportReqParams, commentItem, reportFn, deleteNotify, anonymousFn }: any) {
  const [open, setOpen] = useState(false); // 举报弹窗
  const [deleteOpen, setDeleteOpen] = useState(false); // 删除弹窗
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null); // 弹窗元素
  const [isAnonymousFlag, setIsAnonymousFlag] = useState(commentItem.anonymousFlag); // 是否匿名

  const formRef = useRef<any>(null);
  const currentRef = useRef<HTMLDivElement>(null);
  // 点击举报
  const handleClickOpen = () => {
    sendClickTrack.Productdetail_ReviewsFloor_ReportBtn_Click({
      mainskuid: commentItem.skuId,
    });
    // if (!commentItem?.content) {
    //   UKToast.show({
    //     icon: "warning",
    //     content: t("review.reviewAlert"),
    //   });
    // }
    setOpen(true);
    handlePopoverClose();
  };

  // 删除自己评论
  const onDeleteSubmit = async () => {
    try {
      const res = await handleDeleteComment({
        skuId: commentItem.skuId,
        commentId: commentItem.commentId,
      });
      if (res && res.success) {
        UKToast.show({
          icon: "success",
          content: t("common.message.deleteSuccessful"),
        });
        deleteNotify?.();
      } else {
        UKToast.show({
          icon: "error",
          content: res.msg || t("common.message.deleteFailed"),
        });
      }
      handlePopoverClose();
      setDeleteOpen(false);
    } catch (error) {
      setDeleteOpen(false);
      handlePopoverClose();
      console.error(error);
    }
  };

  // 关闭举报弹窗
  const handleClose = () => {
    setOpen(false);
  };
  // 点击举报
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    sendClickTrack.Productdetail_ReviewsFloor_MoreBtn_Click();
    reviewListSendClickTrack.Productdetail_Comment_Card_Btn({
      skuid: `${commentItem?.skuId || "-100"}`,
      commentId: `${commentItem?.commentId || "-100"}`,
      index: 3,
      button_name: "3",
    });
    const target = event.currentTarget;
    const parentContainer = currentRef.current?.closest(".MuiDialog-container");
    if (parentContainer) {
      parentContainer.removeAttribute("tabindex");
    }
    callAfterLogin(() => {
      setAnchorEl(target);
    });
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  // 举报 提交
  const onFormSubmit = async () => {
    const { content, imageList, videoList } = commentItem || {};
    // 如果评论内容为空，且没有图片和视频，则不支持举报
    if (!content && imageList?.length == 0 && videoList?.length == 0) {
      UKToast.show({
        content: t("review.reviewAlert"),
        icon: "warning",
      });
      return;
    }
    const formData = formRef.current.getFormFieldValue();
    const formParams = Object.keys(formData).reduce((pre, cur) => {
      return { ...pre, [cur]: formData[cur].value };
    }, {});
    delete reportReqParams.parentReplyId;
    const res = await reportComment({
      ...formParams,
      ...(reportReqParams || {}),
    });
    if (res?.success && res?.data?.saveResult) {
      UKToast.show({
        content: (
          <>
            <p>{t("previewResource.reportSuccess")}</p>
            <p>{t("previewResource.reportSuccessThankYou")}</p>
          </>
        ), // 弹窗内容
        icon: "success",
      });
      handleClose();
      reportFn && reportFn();
    } else if (res.code !== "") {
      UKToast.show({
        content: res.msg || (
          <>
            <p>{t("profile.unsubscribe.unsuccess.title")}</p>
            <p>{t("profile.unsubscribe.unsuccess.desc")}</p>
          </>
        ),
        icon: "warning",
      });
    }
    // handleClose();
  };

  const popoverOpen = Boolean(anchorEl);
  const id = popoverOpen ? "simple-popover" : undefined;
  return (
    <Fragment>
      <div className="btn" ref={currentRef} aria-describedby={id} onClick={handleClick}>
        <i className="moreIcon mar"></i>
      </div>
      <IntlPopover id={id} open={popoverOpen} anchorEl={anchorEl} onClose={handlePopoverClose}>
        {commentItem.currentUserOperation && (
          <Typography
            exptag={"exp|" + REVIEW_LIST_EXPOSURE_RECORD.Productdetail_Comment_More_Layer_Expo}
            data-exptag-config={dataExpTagConfig}
            data-exptag-json-param={JSON.stringify({
              skuid: commentItem?.skuId || "-100",
              commentId: commentItem?.commentId || "-100",
              title: "1",
              index: "0",
            })}
            sx={{ fontSize: "14px", padding: "17px 16px", borderRadius: "4px" }}
            className="report-block"
            onClick={() => {
              setDeleteOpen(true);
              reviewListSendClickTrack.Productdetail_Comment_More_Layer({
                skuid: `${commentItem?.skuId || "-100"}`,
                commentId: `${commentItem?.commentId || "-100"}`,
                title: "1",
                index: 1,
              });
            }}
          >
            {t("common.btnText.delete")}
          </Typography>
        )}
        {!commentItem.currentUserOperation && (
          <Typography
            sx={{ fontSize: "14px", padding: "17px 16px", borderRadius: "4px" }}
            className="report-block"
            onClick={() => {
              handleClickOpen();
              reviewListSendClickTrack.Productdetail_Comment_More_Layer({
                skuid: `${commentItem?.skuId || "-100"}`,
                commentId: `${commentItem?.commentId || "-100"}`,
                title: "4",
                index: 4,
              });
              reviewListSendClickTrack.Productdetail_Comment_Report_Win_Expo({
                skuid: `${commentItem?.skuId || "-100"}`,
                commentId: `${commentItem?.commentId || "-100"}`,
                title: "4",
                index: 4,
              });
            }}
          >
            <span
              exptag={"exp|" + REVIEW_LIST_EXPOSURE_RECORD.Productdetail_Comment_More_Layer_Expo}
              data-exptag-config={dataExpTagConfig}
              data-exptag-json-param={JSON.stringify({
                skuid: commentItem?.skuId || "-100",
                commentId: commentItem?.commentId || "-100",
                title: "4",
                index: "1",
              })}
            >
              {t("dp.commentSection.report")}
            </span>
          </Typography>
        )}
      </IntlPopover>
      {/* 举报弹框 */}
      <IntlDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        title={t("dp.reporting.title")}
        content={
          <>
            <div
              className="report-reason-wrapper"
              exptag={"exp|" + REVIEW_DETAIL_EXPOSURE_RECORD.Evaluate_CommentDetail_Report_Win_Expo}
              data-exptag-json-param={JSON.stringify({ skuid: `${commentItem?.skuId}` || "-100", commentId: `${commentItem?.commentId}` })}
              data-exptag-config='{"useClick": false, "stay_duration": 0.6, "repeated":false, "area_rate": 0.01}'
            >
              {t("dp.reporting.placeholder")}
            </div>
            <FormContent ref={formRef} />
          </>
        }
        onSubmit={() => {
          reviewListSendClickTrack.Productdetail_Comment_Report_Win({
            skuid: `${commentItem?.skuId || "-100"}`,
            commentId: `${commentItem?.commentId || "-100"}`,
            index: 1,
            button_name: "2",
          });
          reviewDetailSendClickTrack.Evaluate_CommentDetail_Report_Win({
            skuid: `${commentItem?.skuId || "-100"}`,
            commentId: `${commentItem?.commentId || "-100"}`,
            button_name: "2",
          });
          onFormSubmit();
        }}
        onCancel={() => {
          reviewListSendClickTrack.Productdetail_Comment_Report_Win({
            skuid: `${commentItem?.skuId || "-100"}`,
            commentId: `${commentItem?.commentId || "-100"}`,
            index: 0,
            button_name: "1",
          });
          reviewDetailSendClickTrack.Evaluate_CommentDetail_Report_Win({
            skuid: `${commentItem?.skuId || "-100"}`,
            commentId: `${commentItem?.commentId || "-100"}`,
            button_name: "1",
          });
          handleClose();
        }}
      />
      {/* 删除自己评论弹框的提示 */}
      <IntlDialog
        onClose={() => {
          setDeleteOpen(false);
          handlePopoverClose();
        }}
        aria-labelledby="customized-dialog-title"
        open={deleteOpen}
        title="Delete warning"
        content={
          <>
            <div className="report-reason-wrapper">{t("review.deleteConfirm")}</div>
          </>
        }
        onSubmit={onDeleteSubmit}
        onCancel={() => setDeleteOpen(false)}
      />
    </Fragment>
  );
}
